import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useStaffAttendanceHook } from '../hooks/useStaffAttendanceHook';
import { useStaffAttendanceByUserHook } from '../hooks/useStaffAttendanceByUserHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import {
  TAttendanceByDateRecord,
  TAttendanceByDateResponse,
  TAttendanceByUserResponse,
} from '../../../../apis/__shared/attendance.interfaces';
import dayjs, { Dayjs } from 'dayjs';

export enum ATTENDANCE_VIEW_MODE {
  BY_DATE = 'byDate',
  BY_USER = 'byUser',
}

type TStaffAttendanceAppState = {
  isAttendanceTrackerOpen: boolean;
  isEditMode: boolean;
  isCreateMode: boolean;
  selectedAttendance: TAttendanceByDateRecord | null;
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  attendanceTrackerPayload: TAttendanceByDateResponse;
  dateRange: {
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
  };
  viewMode: ATTENDANCE_VIEW_MODE;
  selectedStaffId?: string;
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_STAFF_ATTENDANCE = 'UPDATE_STAFF_ATTENDANCE',
  UPDATE_ATTENDANCE_TRACKER_PAYLOAD = 'UPDATE_ATTENDANCE_TRACKER_PAYLOAD',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_ATTENDANCE_TRACKER_OPEN = 'SET_IS_ATTENDANCE_TRACKER_OPEN',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_SELECTED_ATTENDANCE = 'SET_SELECTED_ATTENDANCE',
  SET_DATE_RANGE = 'SET_DATE_RANGE',
  SET_VIEW_MODE = 'SET_VIEW_MODE',
  SET_SELECTED_STAFF_ID = 'SET_SELECTED_STAFF_ID',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | {
    type: ACTION_TYPE.UPDATE_STAFF_ATTENDANCE;
    payload: TAttendanceByDateRecord[];
  }
  | {
    type: ACTION_TYPE.UPDATE_ATTENDANCE_TRACKER_PAYLOAD;
    payload: TAttendanceByDateResponse;
  }
  | { type: ACTION_TYPE.SET_IS_ATTENDANCE_TRACKER_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | {
    type: ACTION_TYPE.SET_SELECTED_ATTENDANCE;
    payload: TAttendanceByDateRecord | null;
  }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | {
    type: ACTION_TYPE.SET_DATE_RANGE;
    payload: { fromDate: Dayjs | null; toDate: Dayjs | null };
  }
  | { type: ACTION_TYPE.SET_VIEW_MODE; payload: ATTENDANCE_VIEW_MODE }
  | { type: ACTION_TYPE.SET_SELECTED_STAFF_ID; payload?: string };

const getDefaultDateRange = () => {
  const now = dayjs();
  const startOfMonth = now.startOf('month');
  return {
    fromDate: startOfMonth,
    toDate: now,
  };
};

const initialStaffAttendanceState: TStaffAttendanceAppState = {
  isAttendanceTrackerOpen: false,
  isEditMode: false,
  isCreateMode: false,
  selectedAttendance: null,
  attendanceTrackerPayload: [],
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
  dateRange: getDefaultDateRange(),
  viewMode: ATTENDANCE_VIEW_MODE.BY_DATE,
  selectedStaffId: undefined,
};

type TState = {
  staffAttendanceAppState: TStaffAttendanceAppState;
  staffAttendanceRecords: TAttendanceByDateResponse;
  staffAttendanceByUserRecords: TAttendanceByUserResponse;
  attendanceTrackerList: TAttendanceByDateRecord[];
};

const StaffAttendanceContext = createContext<TState | undefined>(undefined);
const StaffAttendanceDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const staffAttendanceReducer = (
  appState: TStaffAttendanceAppState,
  action: Action
): TStaffAttendanceAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...appState,
        userParams: { ...appState.userParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_STAFF_ATTENDANCE:
      return { ...appState, ...action.payload };
    case ACTION_TYPE.UPDATE_ATTENDANCE_TRACKER_PAYLOAD:
      return { ...appState, attendanceTrackerPayload: action.payload };
    case ACTION_TYPE.SET_IS_ATTENDANCE_TRACKER_OPEN:
      return { ...appState, isAttendanceTrackerOpen: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...appState, isEditMode: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...appState, isCreateMode: action.payload };
    case ACTION_TYPE.SET_SELECTED_ATTENDANCE:
      return { ...appState, selectedAttendance: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_DATE_RANGE:
      return { ...appState, dateRange: action.payload };
    case ACTION_TYPE.SET_VIEW_MODE:
      return { ...appState, viewMode: action.payload };
    case ACTION_TYPE.SET_SELECTED_STAFF_ID:
      return { ...appState, selectedStaffId: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StaffAttendanceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [staffAttendanceAppState, dispatch] = useReducer(
    staffAttendanceReducer,
    initialStaffAttendanceState
  );

  const {
    data: staffAttendanceResponse,
    isLoading: staffAttendanceResponseLoading,
    isError: staffAttendanceResponseLoadingError,
    isSuccess: staffAttendanceResponseLoadingDataSuccess,
    error: staffAttendanceResponseDataError,
  } = useStaffAttendanceHook(
    staffAttendanceAppState.userParams,
    staffAttendanceAppState.dateRange
  );

  const {
    data: staffAttendanceByUserResponse,
    isLoading: staffAttendanceByUserResponseLoading,
    isError: staffAttendanceByUserResponseLoadingError,
    isSuccess: staffAttendanceByUserResponseLoadingDataSuccess,
    error: staffAttendanceByUserResponseDataError,
  } = useStaffAttendanceByUserHook(
    staffAttendanceAppState.userParams,
    staffAttendanceAppState.dateRange,
    staffAttendanceAppState.selectedStaffId,
    {
      enabled:
        staffAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_USER,
    }
  );

  const loadingPageData = useMemo(
    () =>
      staffAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_DATE
        ? staffAttendanceResponseLoading
        : staffAttendanceByUserResponseLoading,
    [
      staffAttendanceAppState.viewMode,
      staffAttendanceResponseLoading,
      staffAttendanceByUserResponseLoading,
    ]
  );

  const loadingPageDataSuccess = useMemo(
    () =>
      staffAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_DATE
        ? staffAttendanceResponseLoadingDataSuccess
        : staffAttendanceByUserResponseLoadingDataSuccess,
    [
      staffAttendanceAppState.viewMode,
      staffAttendanceResponseLoadingDataSuccess,
      staffAttendanceByUserResponseLoadingDataSuccess,
    ]
  );

  const loadingPageDataError = useMemo(
    () =>
      staffAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_DATE
        ? staffAttendanceResponseLoadingError
        : staffAttendanceByUserResponseLoadingError,
    [
      staffAttendanceAppState.viewMode,
      staffAttendanceResponseLoadingError,
      staffAttendanceByUserResponseLoadingError,
    ]
  );

  useEffect(() => {
    onStateChangeHandler();
    // eslint-disable-next-line
  }, [
    staffAttendanceResponseDataError,
    staffAttendanceByUserResponseDataError,
    loadingPageDataError,
    staffAttendanceResponseLoading,
    staffAttendanceByUserResponseLoading,
    loadingPageData,
    loadingPageDataSuccess,
    staffAttendanceResponseLoadingError,
    staffAttendanceByUserResponseLoadingError,
    staffAttendanceAppState.viewMode,
  ]);

  const onStateChangeHandler = () => {
    if (loadingPageData) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (loadingPageDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (loadingPageDataError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: ERROR_MESSAGE.GET_STAFF_LIST_FAILURE,
      });
    }
  };

  const currentState = useMemo<TState>(
    () => ({
      staffAttendanceAppState,
      staffAttendanceRecords: staffAttendanceResponse ?? [],
      staffAttendanceByUserRecords: staffAttendanceByUserResponse ?? {},
      attendanceTrackerList: [],
    }),
    [
      staffAttendanceAppState,
      staffAttendanceResponse,
      staffAttendanceByUserResponse,
    ]
  );

  return (
    <StaffAttendanceDispatchContext.Provider value={dispatch}>
      <StaffAttendanceContext.Provider value={currentState}>
        {children}
      </StaffAttendanceContext.Provider>
    </StaffAttendanceDispatchContext.Provider>
  );
};

export const useStaffAttendanceState = () => {
  const state = useContext(StaffAttendanceContext);
  if (state === undefined) {
    throw new Error(
      'useStaffAttendanceState must be used within a StaffAttendanceProvider'
    );
  }
  return state;
};

export const useStaffAttendanceActions = () => {
  const dispatch = useContext(StaffAttendanceDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStaffAttendanceActions dispatch must be within a StaffAttendanceProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const updateAttendanceTrackerPayload = (
    payload: TAttendanceByDateResponse
  ) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_ATTENDANCE_TRACKER_PAYLOAD,
      payload: payload,
    });
  };

  const setIsAttendanceTrackerOpen = (isOpen: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_ATTENDANCE_TRACKER_OPEN,
      payload: isOpen,
    });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setSelectedAttendance = (
    attendance: TAttendanceByDateRecord | null
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_ATTENDANCE,
      payload: attendance,
    });
  };

  const setDateRange = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
    dispatch({
      type: ACTION_TYPE.SET_DATE_RANGE,
      payload: { fromDate, toDate },
    });
  };

  const setViewMode = (viewMode: ATTENDANCE_VIEW_MODE) => {
    dispatch({
      type: ACTION_TYPE.SET_VIEW_MODE,
      payload: viewMode,
    });
  };

  const setSelectedStaffId = (staffId?: string) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_STAFF_ID,
      payload: staffId,
    });
  };

  return {
    updateSearchParams,
    updateAttendanceTrackerPayload,
    setIsAttendanceTrackerOpen,
    setIsEditMode,
    setIsCreateMode,
    setSelectedAttendance,
    setDateRange,
    setViewMode,
    setSelectedStaffId,
  };
};
